/* Customall personaliser for the clip-art product page.
 *
 * Executes a Customall campaign (catalogue schema cm1, written by scripts/extract.py) the way
 * Customall's own storefront sdk does, on Konva - the library that sdk draws with - so text
 * metrics, stacking and geometry come out the same. The rules and where they were read:
 *
 *   drawing order   the template's own layer order                          (sdk artwork `vl`)
 *   form order      settings.ordering[artwork][template]                     (sdk `layers()`)
 *   visibility      layer.condition, recursive through the watched layer     (sdk `gn`)
 *   images          stretched into x/y/width/height; crop is ignored         (sdk `configImage`)
 *   text            prefix + value + suffix; autofit to the box, capped by
 *                   max_font_size; vertical ink centring when useOffset      (sdk text layer, `Tn`)
 *   print area      group at the mockup's print area, artwork scaled to its
 *                   displayed width, clipped by the clipping mask            (sdk print-area group)
 *   stroke          layer stroke, width scaled like x/y, fill after stroke   (sdk text config)
 *   areas           one artwork + mockup per print area, picked by the
 *                   variant; a variant the campaign does not list follows a
 *                   listed one with the same first option (seen on the source)
 *
 * It drives the clip-art page's DOM contract, the same hooks ca-builder.js drives:
 * [data-ca-stage] [data-ca-options] [data-ca-fields] [data-ca-price] [data-ca-form] [data-ca-cta]
 * [data-ca-id] [data-ca-err], properties written as inputs marked data-ca-prop on submit.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ca][data-cm-src]');
  if (!root || root.dataset.cmBooted) return;
  root.dataset.cmBooted = '1';

  function readJSON(sel) {
    var n = root.querySelector(sel);
    try { return n ? JSON.parse(n.textContent) : null; } catch (e) { return null; }
  }
  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  }
  function fail(e) {
    console.error('[cm-builder]', e);
    var err = root.querySelector('[data-ca-err]');
    if (err) { err.textContent = 'The personaliser could not load. Please refresh.'; err.hidden = false; }
  }

  var PRODUCT = readJSON('[data-ca-product]') || { variants: [], options: [], images: [] };

  function whenKonva(go, tries) {
    if (window.Konva) return go();
    if ((tries || 0) > 200) return fail(new Error('Konva did not load'));
    setTimeout(function () { whenKonva(go, (tries || 0) + 1); }, 50);
  }

  fetch(root.getAttribute('data-cm-src'), { credentials: 'omit' })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .then(function (P) {
      if (P.schema !== 'cm1') throw new Error('payload schema ' + P.schema + ' is not cm1');
      whenKonva(function () { run(P); });
    })
    .catch(fail);

  /* ----------------------------------------------------------- Customall rules */

  // sdk `gn`: a rule only holds if the layer it watches is itself shown
  function holds(layer, data, layers) {
    // {enable: false, rules: [], ...condition}: a condition without `enable` never applies
    var c = Object.assign({ enable: false, rules: [] }, layer.condition || {});
    var rules = (c.rules || []).filter(Boolean);
    if (c.enable === false || !rules.length) return true;
    var res = rules.map(function (r) {
      var watched = layers.filter(function (l) { return l.id === r.option; })[0];
      if (watched && !holds(watched, data, layers)) return false;
      var got = data[r.option];
      var obj = r.value && typeof r.value === 'object';
      switch (r.logic) {
        case '=': return obj ? keyOf(got) === r.value.key : got === r.value;
        case '!!': return !!got;
        default: return obj ? keyOf(got) !== r.value.key : got !== r.value;
      }
    });
    var ok = false;
    if (c.match === 'all') ok = res.every(Boolean);
    else if (c.match === 'one') ok = res.some(Boolean);
    else if (c.match === 'none') ok = res.every(function (x) { return !x; });
    return c.action === 'show' ? ok : !ok;
  }
  function keyOf(v) { return v && typeof v === 'object' ? v.key : undefined; }

  function textOf(layer, data) {
    var v = data[layer.id];
    var t = layer.text || '';
    if (v && typeof v === 'object') { if (typeof v.text !== 'undefined') t = v.text; }
    else if (typeof v !== 'undefined') t = v;
    var f = layer.field;
    if (f) t = (f.prefix || '') + t + (f.suffix || '');
    if (layer.textTransform === 'uppercase') t = t.toUpperCase();
    else if (layer.textTransform === 'lowercase') t = t.toLowerCase();
    return t;
  }

  // sdk `An`: where the ink of a face sits inside a middle-aligned box, as a share of the size
  var OFFSETS = {};
  function inkOffset(family) {
    if (OFFSETS[family] !== undefined) return OFFSETS[family];
    var size = 300, w = 6 * size, h = 2 * size, W = 2 * w + 50, H = 2 * h + 50;
    var box = document.createElement('div');
    var ratio = Konva.pixelRatio;
    Konva.pixelRatio = 1;
    var stage = new Konva.Stage({ container: box, width: W, height: H });
    var layer = new Konva.Layer();
    layer.add(new Konva.Text({ text: 'A1g2h3', fontFamily: family, fontSize: size, fill: '#fff', verticalAlign: 'middle',
      width: w, height: h, wrap: 'none', ellipsis: false, x: 50, y: 50 }));
    stage.add(layer);
    var out = 0;
    try {
      var px = layer.getCanvas()._canvas.getContext('2d').getImageData(0, 0, W, H).data;
      var top = null, bottom = null;
      for (var i = 0; i < px.length; i += 4) {
        if (px[i + 3] !== 0) {
          var row = ~~(i / 4 / W);
          if (top === null) top = row;
          if (bottom === null || bottom < row) bottom = row;
        }
      }
      out = top === null ? 0 : -1 * (h / 2 + 50 - ((bottom - top) / 2 + top)) / size;
    } catch (e) { out = 0; }
    stage.destroy();
    Konva.pixelRatio = ratio;
    OFFSETS[family] = out;
    return out;
  }

  // sdk text layer `onTextChange`: scale the size so the widest line and all lines fit the box
  function autofit(node, layer, e) {
    var f = layer.field || {};
    var lines = String(node.text()).split('\n');
    if (lines.join('') === '') return;
    var stroke = layer.strokeEnabled && node.strokeWidth() || 0;
    var widest = Math.max.apply(null, lines.map(function (t) { return node.measureSize(t).width + 2 * stroke; }));
    var k = Math.min(node.width() / widest, node.height() / (lines.length * node.textHeight));
    var size = node.fontSize() * k;
    var max = f.maxFontSize ? f.maxFontSize * e : 0;
    var min = f.minFontSize ? f.minFontSize * e : 0;
    if (min && size < min) size = min;
    else if (max && size > max) size = max;
    node.fontSize(size);
  }

  /* ------------------------------------------------------------------- the page */

  function run(P) {
    var aw = P.artwork;
    var mock = P.mockup;
    var pa = mock.printarea;
    var areas = P.areas && P.areas.length > 1 ? P.areas : null;
    var area = areas ? areas[0] : null;          // P.artwork and P.mockup are the first area's
    var tpl = Math.min(P.defaultTemplate || 0, aw.templates.length - 1);
    var data = JSON.parse(JSON.stringify(P.defaults || {}));
    var typed = {};                 // what the shopper typed; the preview shows the default until then
    var picked = {};                // clip-art the shopper chose (not a default): kept across garments
    var showRequired = false;
    var fontsReady = false;

    var stageEl = root.querySelector('[data-ca-stage]');
    var fieldsEl = root.querySelector('[data-ca-fields]');
    var optsEl = root.querySelector('[data-ca-options]');
    var priceEl = root.querySelector('[data-ca-price]');
    var cta = root.querySelector('[data-ca-cta]');
    var errEl = root.querySelector('[data-ca-err]');
    var form = root.querySelector('[data-ca-form]');
    var idInput = root.querySelector('[data-ca-id]');

    // Art is stored at full size (the print needs it); the page asks the cdn for what it shows.
    var ART = 1600;
    function url(key, width) {
      if (!key) return null;
      var w = width || ART;
      var u = P.assets && P.assets[key];
      if (u) return /cdn\.shopify\.com|\/cdn\/shop\//.test(u) ? u + (u.indexOf('?') < 0 ? '?' : '&') + 'width=' + w : u;
      return customallUrl(key, w);
    }
    function template() { return aw.templates[tpl]; }
    function layers() { return template().layers; }
    function byId(id) { return layers().filter(function (l) { return l.id === id; })[0]; }
    function shown(layer) { return layer.excludeMockup !== true && holds(layer, data, layers()); }
    function inForm() {
      return template().form.map(byId).filter(function (l) { return l && l.field && shown(l); });
    }
    function category(layer) { return (P.categories || {})[layer.field.category] || { items: [] }; }

    // sdk cliparts-by-category: a value is kept only if its item belongs to this layer's
    // category; otherwise the layer's own default for that category, when defaults are on.
    function settleDefaults() {
      layers().forEach(function (l) {
        var f = l.field;
        if (!f || f.kind !== 'clipartCategory') return;
        var items = category(l).items;
        var v = data[l.id];
        if (v && typeof v === 'object' && items.some(function (i) { return i.id === v.id; })) return;
        var it = f.enableDefault && f.defaultItem && items.filter(function (i) { return i.id === f.defaultItem; })[0];
        if (it) data[l.id] = { categoryID: f.category, id: it.id, key: it.key };
        else delete data[l.id];
      });
    }
    function chosenItem(layer) {
      var k = keyOf(data[layer.id]);
      return category(layer).items.filter(function (i) { return i.key === k; })[0] || null;
    }

    /* ---- print areas: the garment the variant names draws its own artwork on its own mockup ---- */
    // by id on the source; by option values on a clone, which is a new product with new ids
    function listed(v) {
      var id = String(v.id);
      return areas.filter(function (a) {
        return a.variants.indexOf(id) >= 0 || (a.options || []).some(function (o) {
          return o.every(function (x, i) { return v['option' + (i + 1)] === x; });
        });
      })[0] || null;
    }
    function areaFor(v) {
      if (!areas || !v) return null;
      var a = listed(v);
      if (a) return a;
      var twin = (PRODUCT.variants || []).filter(function (w) { return w.option1 === v.option1 && listed(w); })[0];
      return twin ? listed(twin) : areas[0];
    }
    // the shopper's typed text and picks carry over by layer id; the new artwork's defaults fill the rest
    function useArea(a) {
      if (!a || a === area || !(P.artworks || {})[a.artwork] || !a.mockup) return false;
      area = a;
      aw = P.artworks[a.artwork];
      mock = a.mockup;
      pa = mock.printarea;
      tpl = Math.min(a.defaultTemplate || 0, aw.templates.length - 1);
      var before = data;
      data = JSON.parse(JSON.stringify(a.defaults || {}));
      Object.keys(before).forEach(function (id) { if (picked[id] && keyOf(before[id])) data[id] = before[id]; });
      Object.keys(typed).forEach(function (id) { data[id] = { text: typed[id] }; });
      return true;
    }

    /* ---- fonts ---- */
    // The ink offset is measured on the face itself, so nothing is drawn until every face has
    // really loaded: document.fonts.check() also answers true while no @font-face matches yet.
    (function loadFonts() {
      var fams = P.fonts || [];
      var wait = function (f) {
        return new Promise(function (res) {
          var t0 = Date.now();
          var again = function () {
            document.fonts.load('300px "' + f + '"').then(function (faces) {
              if (faces && faces.length) return res();
              if (Date.now() - t0 > 15000) { console.warn('[cm-builder] font did not load', f); return res(); }
              setTimeout(again, 100);
            }, function () { setTimeout(again, 200); });
          };
          var file = (P.fontFiles || {})[f];
          if (file) {                              // the face rehosted on the store's cdn
            var st = document.createElement('style');
            st.textContent = '@font-face{font-family:"' + f + '";src:url("' + file + '");font-display:block}';
            document.head.appendChild(st);
            return again();
          }
          var href = 'https://apis-v2.customall.io/app/f/' + f + '.css';
          var link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = again;
          link.onerror = function () { console.warn('[cm-builder] font css failed', href); res(); };
          document.head.appendChild(link);
        });
      };
      if (!document.fonts) { fontsReady = true; return; }
      Promise.all(fams.map(wait)).then(function () { fontsReady = true; draw(); });
    })();

    /* ---- gallery: slide 1 is the live design, then the product's photos ---- */
    var slides = [], thumbs = [], counter = null, canvasEl = null, stage = null, scene = null;
    var mainEl = null, fitStage = null;
    function previewUrl() {
      return url((area && area.previewImage) || P.previewImage) || url((mock.under[0] || {}).key);
    }
    // another garment: its mockup's shape and its preview as the first thumbnail
    function restage() {
      if (!mainEl) return;
      mainEl.style.aspectRatio = mock.width + ' / ' + mock.height;
      var im = thumbs[0] && thumbs[0].querySelector('img');
      if (im) im.src = previewUrl() || '';
      if (fitStage) fitStage();
    }
    function buildStage() {
      stageEl.textContent = '';
      var gallery = el('div', 'ca-gallery');
      var main = el('div', 'ca-main');
      mainEl = main;
      main.style.aspectRatio = mock.width + ' / ' + mock.height;
      var photos = (PRODUCT.images || []).slice();
      var count = 1 + photos.length;
      for (var i = 0; i < count; i++) {
        var slide = el('div', 'ca-slide');
        slide.setAttribute('data-slide', i);
        if (i > 0) {
          var ph = el('img', 'ca-photo');
          ph.src = photos[i - 1]; ph.alt = ''; ph.loading = 'lazy';
          slide.appendChild(ph);
        }
        main.appendChild(slide);
        slides.push(slide);
      }
      if (count > 1) { counter = el('span', 'ca-count'); main.appendChild(counter); }
      gallery.appendChild(main);
      if (count > 1) {
        var strip = el('div', 'ca-thumbs');
        var thumbUrls = [previewUrl()].concat(photos);
        thumbUrls.forEach(function (u, k) {
          var b = el('button', 'ca-thumb');
          b.type = 'button';
          b.setAttribute('aria-label', 'Image ' + (k + 1));
          var im = el('img'); im.src = u || ''; im.alt = ''; im.loading = 'lazy';
          b.appendChild(im);
          b.addEventListener('click', function () { showSlide(k); });
          strip.appendChild(b);
          thumbs.push(b);
        });
        gallery.appendChild(strip);
      }
      stageEl.appendChild(gallery);
      canvasEl = el('div', 'ca-canvas ca-overlay');
      slides[0].appendChild(canvasEl);
      stage = new Konva.Stage({ container: canvasEl, width: 10, height: 10, listening: false });
      scene = new Konva.Layer({ listening: false });
      stage.add(scene);
      var fit = function () {
        var w = canvasEl.clientWidth || main.clientWidth;
        if (!w) return;
        stage.width(w);
        stage.height(w * mock.height / mock.width);
        stage.scale({ x: w / mock.width, y: w / mock.width });
        stage.batchDraw();
      };
      fitStage = fit;
      if (window.ResizeObserver) new ResizeObserver(fit).observe(main);
      window.addEventListener('resize', fit);
      fit();
      showSlide(0);
    }
    function showSlide(k) {
      slides.forEach(function (sl, i) { sl.hidden = i !== k; });
      thumbs.forEach(function (t, i) { t.setAttribute('aria-current', i === k ? 'true' : 'false'); });
      if (counter) counter.textContent = (k + 1) + '/' + slides.length;
      if (k === 0 && stage) { stage.width(canvasEl.clientWidth || stage.width()); stage.batchDraw(); }
    }

    /* ---- images, loaded once per url ---- */
    var IMG = {};
    function image(u) {
      if (!u) return Promise.resolve(null);
      if (!IMG[u]) {
        IMG[u] = new Promise(function (res) {
          var im = new window.Image();
          im.crossOrigin = 'Anonymous';
          im.onload = function () { res(im); };
          im.onerror = function () { res(null); };
          im.src = u;
        });
      }
      return IMG[u];
    }

    /* ---- the scene ---- */
    var drawSeq = 0;
    function draw() {
      if (!scene) return;
      var seq = ++drawSeq;
      var e = pa.width * pa.sx / aw.width;
      var list = layers().filter(function (l) { return shown(l); });
      var wants = [];
      mock.under.concat(mock.over).forEach(function (m) { wants.push(image(url(m.key))); });
      list.forEach(function (l) {
        if (l.type !== 'Image') return;
        var key = l.field ? keyOf(data[l.id]) : l.key;
        wants.push(image(url(key)));
      });
      Promise.all(wants).then(function () {
        if (seq !== drawSeq) return;          // a newer choice is already drawing
        scene.destroyChildren();
        var add = function (parent, m) {
          return image(url(m.key)).then(function (im) {
            if (im) parent.add(new Konva.Image({ image: im, x: m.x, y: m.y, width: m.width, height: m.height }));
          });
        };
        var chain = Promise.resolve();
        mock.under.forEach(function (m) { chain = chain.then(function () { return add(scene, m); }); });
        chain.then(function () {
          var g = new Konva.Group({ id: pa.id || 'print-area', name: 'print-area', x: pa.x, y: pa.y, rotation: pa.rotation || 0 });
          var off = new Konva.Group({ x: pa.offsetX || 0 });
          var clip = pa.clip;
          var clipped = new Konva.Group(clip ? {
            clipFunc: function (ctx) {
              ctx.rect(clip.x * pa.sx - (pa.offsetX || 0), clip.y * pa.sy, clip.width * pa.sx, clip.height * pa.sy);
            }
          } : {});
          var art = new Konva.Group();
          clipped.add(art); off.add(clipped); g.add(off); scene.add(g);
          var ps = list.map(function (l) {
            if (l.type === 'Image') {
              var key = l.field ? keyOf(data[l.id]) : l.key;
              return image(url(key)).then(function (im) {
                return im ? new Konva.Image({ id: l.id, artKey: key, image: im, x: l.x * e, y: l.y * e, width: l.width * e, height: l.height * e,
                  rotation: l.rotation || 0, scaleX: l.scaleX || 1, scaleY: l.scaleY || 1, opacity: l.opacity == null ? 1 : l.opacity }) : null;
              });
            }
            if (!fontsReady) return Promise.resolve(null);
            var node = new Konva.Text({ id: l.id, x: l.x * e, y: l.y * e, width: l.width * e, height: l.height * e,
              rotation: l.rotation || 0, scaleX: l.scaleX || 1, scaleY: l.scaleY || 1,
              fontFamily: l.fontFamily, fontSize: ((l.autofit && l.actualFontSize) || l.fontSize) * e,
              fill: l.fill || '#000000', align: l.align || 'left', letterSpacing: (l.letterSpacing || 0) * e,
              lineHeight: l.lineHeight || 1, verticalAlign: l.verticalAlign || 'middle', wrap: 'none',
              fontStyle: l.fontStyle || 'normal', text: textOf(l, data) });
            if (l.strokeEnabled && l.stroke) {
              node.setAttrs({ stroke: l.stroke, strokeWidth: (l.strokeWidth || 0) * e,
                fillAfterStrokeEnabled: !!l.fillAfterStrokeEnabled });
              if (l.lineJoin) node.lineJoin(l.lineJoin);
            }
            if (l.autofit) autofit(node, l, e);
            if (aw.useOffset) node.offsetY(inkOffset(l.fontFamily) * node.fontSize());
            // sdk wraps every text in its own group
            var tg = new Konva.Group();
            tg.add(node);
            return Promise.resolve(tg);
          });
          return Promise.all(ps).then(function (nodes) {
            if (seq !== drawSeq) return;
            nodes.forEach(function (n) { if (n) art.add(n); });
          });
        }).then(function () {
          if (seq !== drawSeq) return;
          var c2 = Promise.resolve();
          mock.over.forEach(function (m) { c2 = c2.then(function () { return add(scene, m); }); });
          return c2;
        }).then(function () {
          if (seq !== drawSeq) return;
          scene.batchDraw();
          root.dispatchEvent(new CustomEvent('cm:drawn', { bubbles: true }));
        });
      });
    }

    /* ---- the form ---- */
    function templateRow() {
      var r = el('div', 'ca-opt cm-tpl');
      r.setAttribute('data-opt-id', 'template');
      var name = el('div', 'ca-opt-name');
      name.appendChild(el('span', 'ca-opt-label', aw.label));
      r.appendChild(name);
      var wrap = el('div', 'ca-swatches');
      wrap.setAttribute('role', 'radiogroup');
      aw.templates.forEach(function (t, k) {
        var b = el('button', 'ca-sw');
        b.type = 'button';
        b.setAttribute('data-value-id', 'template:' + k);
        b.setAttribute('aria-pressed', k === tpl ? 'true' : 'false');
        b.setAttribute('aria-label', t.title);
        b.title = t.title;
        if (t.thumb && aw.mode === 'image') {
          var im = el('img'); im.src = url(t.thumb, 200); im.alt = t.title; im.loading = 'lazy';
          b.appendChild(im);
        } else {
          b.classList.add('ca-sw-text');
          b.textContent = t.title;
        }
        b.addEventListener('click', function () { tpl = k; settleDefaults(); renderFields(); draw(); });
        wrap.appendChild(b);
      });
      r.appendChild(wrap);
      return r;
    }

    function missing(l) {
      var f = l.field;
      if (!f.required && f.kind !== 'clipartCategory') return false;
      if (f.kind === 'text') return !String(typed[l.id] || '').length;
      return !keyOf(data[l.id]);
    }

    function row(l) {
      var f = l.field;
      var r = el('div', 'ca-opt');
      r.setAttribute('data-opt-id', l.id);
      var name = el('div', 'ca-opt-name');
      name.appendChild(el('span', 'ca-opt-label', f.label));
      var counter = null;
      if (f.kind === 'text' && f.max) {
        counter = el('span', 'ca-counter', ' (' + String(typed[l.id] || '').length + '|' + f.max + ')');
        name.appendChild(counter);
      }
      if (f.required || f.kind === 'clipartCategory') {
        var req = el('span', 'ca-req', ' *');
        var msg = el('span', 'ca-req-msg', f.label + ' is required');
        msg.hidden = !(showRequired && missing(l));
        req.appendChild(msg);
        name.appendChild(req);
      }
      r.appendChild(name);
      if (f.kind === 'text') {
        var inp = el('input', 'ca-input');
        inp.type = 'text';
        if (f.max) inp.maxLength = f.max;
        inp.placeholder = f.placeholder || '';
        inp.value = typed[l.id] || '';
        inp.addEventListener('input', function () {
          var v = inp.value;
          if (f.max && v.length > f.max) { v = v.slice(0, f.max); inp.value = v; }
          typed[l.id] = v;
          data[l.id] = { text: v };
          if (counter) counter.textContent = ' (' + v.length + '|' + f.max + ')';
          if (formKey() !== lastKey) renderFields();
          draw();
        });
        r.appendChild(inp);
      } else {
        var wrap = el('div', 'ca-swatches');
        wrap.setAttribute('role', 'radiogroup');
        var now = keyOf(data[l.id]);
        category(l).items.forEach(function (it) {
          var b = el('button', 'ca-sw');
          b.type = 'button';
          b.setAttribute('data-value-id', it.id);
          b.setAttribute('aria-pressed', it.key === now ? 'true' : 'false');
          b.setAttribute('aria-label', it.title || it.id);
          if (it.title) b.title = it.title;
          var im = el('img'); im.src = url(it.thumb || it.key, 200); im.alt = it.title || ''; im.loading = 'lazy';
          b.appendChild(im);
          b.addEventListener('click', function () {
            data[l.id] = { categoryID: l.field.category, id: it.id, key: it.key };
            picked[l.id] = true;
            if (formKey() !== lastKey) renderFields();
            else Array.prototype.forEach.call(wrap.children, function (x) {
              x.setAttribute('aria-pressed', x === b ? 'true' : 'false');
            });
            draw();
          });
          wrap.appendChild(b);
        });
        r.appendChild(wrap);
      }
      if (f.help) r.appendChild(el('small', 'ca-help', f.help));
      return r;
    }

    var lastKey = '';
    function formKey() { return tpl + '|' + inForm().map(function (l) { return l.id; }).join(','); }

    function renderFields() {
      var a = document.activeElement;
      var focusId = a && a.classList && a.classList.contains('ca-input') && a.closest('.ca-opt') ? a.closest('.ca-opt').getAttribute('data-opt-id') : null;
      var pos = focusId ? a.selectionStart : 0;
      fieldsEl.textContent = '';
      // sdk form: the picker goes first when templateIndex is empty or 0, else right after the
      // templateIndex-th layer of the form list (or after the last one if the list is shorter)
      var many = aw.templates.length > 1;
      var at = template().pickerAt;
      if (many && !at) fieldsEl.appendChild(templateRow());
      var list = template().form;
      list.forEach(function (id, i) {
        var l = byId(id);
        if (l && l.field && shown(l)) fieldsEl.appendChild(row(l));
        if (many && at && (at === i + 1 || (at > i && i === list.length - 1))) fieldsEl.appendChild(templateRow());
      });
      lastKey = formKey();
      if (focusId) {
        var again = fieldsEl.querySelector('.ca-opt[data-opt-id="' + focusId + '"] .ca-input');
        if (again) { again.focus(); try { again.setSelectionRange(pos, pos); } catch (e) { /* ignore */ } }
      }
    }

    /* ---- variants and price ---- */
    var selVariant = {};
    (PRODUCT.options || []).forEach(function (o) { selVariant[o.name] = o.values[0]; });
    function variantFor(s) {
      return (PRODUCT.variants || []).filter(function (v) {
        return PRODUCT.options.every(function (o, i) { return v['option' + (i + 1)] === s[o.name]; });
      })[0] || null;
    }
    function money(c) {
      var fmt = PRODUCT.moneyFormat;
      if (fmt && window.Shopify && typeof Shopify.formatMoney === 'function') {
        try { return String(Shopify.formatMoney(c, fmt)).replace(/<[^>]*>/g, ''); } catch (e) { /* fall through */ }
      }
      var amount = (c / 100).toFixed(2);
      return fmt ? fmt.replace(/<[^>]*>/g, '').replace(/\{\{\s*\w+\s*\}\}/, amount) : '$' + amount;
    }
    // the chosen value makes a combination the product does not sell (Hoodie has no 6XL): land on
    // the variant with that value that keeps most of the other choices, an available one first
    function snap(name, val) {
      var k = (PRODUCT.options || []).map(function (o) { return o.name; }).indexOf(name);
      var best = null, score = -1;
      (PRODUCT.variants || []).forEach(function (v) {
        if (v['option' + (k + 1)] !== val) return;
        var s = v.available ? 100 : 0;
        PRODUCT.options.forEach(function (o, i) { if (v['option' + (i + 1)] === selVariant[o.name]) s++; });
        if (s > score) { score = s; best = v; }
      });
      if (best) PRODUCT.options.forEach(function (o, i) { selVariant[o.name] = best['option' + (i + 1)]; });
    }
    function renderVariants() {
      if (!optsEl) return;
      optsEl.textContent = '';
      (PRODUCT.options || []).forEach(function (o) {
        if (o.name === 'Title' && o.values.length === 1 && o.values[0] === 'Default Title') return;
        var f = el('div', 'ca-field');
        f.appendChild(el('span', 'ca-label', o.name));
        var wrap = el('div', 'ca-chips');
        o.values.forEach(function (val) {
          var b = el('button', 'ca-chip', val);
          b.type = 'button';
          b.setAttribute('data-value', val);
          b.setAttribute('aria-pressed', selVariant[o.name] === val ? 'true' : 'false');
          b.disabled = !(PRODUCT.variants || []).some(function (v) {
            return v['option' + (PRODUCT.options.indexOf(o) + 1)] === val && v.available;
          });
          b.addEventListener('click', function () {
            selVariant[o.name] = val;
            if (!variantFor(selVariant)) snap(o.name, val);
            renderVariants();
            renderPrice();
            if (useArea(areaFor(variantFor(selVariant)))) { settleDefaults(); renderFields(); restage(); draw(); }
          });
          wrap.appendChild(b);
        });
        f.appendChild(wrap);
        optsEl.appendChild(f);
      });
    }
    function renderPrice() {
      var v = variantFor(selVariant);
      if (!v) return;
      priceEl.textContent = money(v.price);
      if (idInput) idInput.value = v.id;
      if (cta) {
        cta.disabled = !v.available;
        cta.textContent = v.available ? (cta.getAttribute('data-label') || 'Add to cart') : 'Sold out';
      }
    }

    /* ---- the order line ---- */
    function properties() {
      var out = {};
      if (aw.templates.length > 1) out[aw.label] = template().title;
      inForm().forEach(function (l) {
        var f = l.field;
        var value = f.kind === 'text' ? (typed[l.id] || '') : ((chosenItem(l) || {}).title || keyOf(data[l.id]) || '');
        if (value === '') return;
        var label = f.label, n = 2;
        while (out[label] !== undefined) label = f.label + ' (' + (n++) + ')';
        out[label] = value;
      });
      return out;
    }
    function design() {
      var picked = {};
      inForm().forEach(function (l) {
        picked[l.id] = l.field.kind === 'text' ? { text: typed[l.id] || '' } : data[l.id];
      });
      return { v: 'cm1', artwork: aw.id, template: tpl, data: picked };
    }

    form.addEventListener('submit', function (ev) {
      var gaps = inForm().filter(missing);
      if (gaps.length) {
        ev.preventDefault();
        showRequired = true;
        renderFields();
        var first = fieldsEl.querySelector('.ca-opt[data-opt-id="' + gaps[0].id + '"]');
        if (first && first.scrollIntoView) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (errEl) errEl.hidden = true;
      form.querySelectorAll('[data-ca-prop]').forEach(function (n) { n.remove(); });
      var add = function (name, value) {
        var input = document.createElement('input');
        input.type = 'hidden'; input.name = name; input.value = value;
        input.setAttribute('data-ca-prop', '');
        form.appendChild(input);
      };
      var props = properties();
      Object.keys(props).forEach(function (k) { add('properties[' + k + ']', props[k]); });
      add('properties[_design]', JSON.stringify(design()));
    });

    // for parity and QA, and for debugging from the console
    window.cmBuilder = {
      properties: properties,
      design: design,
      state: function () {
        return layers().filter(shown).map(function (l) {
          return l.type === 'Image'
            ? { id: l.id, type: 'Image', key: l.field ? keyOf(data[l.id]) : l.key }
            : { id: l.id, type: 'Text', text: textOf(l, data) };
        });
      },
      nodes: function () {
        return stage ? stage.find(function (n) { return n.getClassName() === 'Image' || n.getClassName() === 'Text'; })
          .filter(function (n) { return n.id() && n.findAncestor('.print-area'); })
          .map(function (n) {
            var r = { id: n.id(), cls: n.getClassName(), x: n.x(), y: n.y(), w: n.width(), h: n.height(), rot: n.rotation() };
            if (n.getClassName() === 'Text') {
              r.text = n.text(); r.fontSize = n.fontSize(); r.offsetY = n.offsetY();
              r.stroke = n.stroke() || null; r.strokeWidth = n.stroke() ? n.strokeWidth() : 0;
              r.fillAfterStroke = !!n.fillAfterStrokeEnabled(); r.lineJoin = n.lineJoin() || null;
            }
            else r.key = n.getAttr('artKey');
            return r;
          }) : [];
      },
      stage: function () { return stage; },
      template: function (k) { if (k != null) { tpl = k; settleDefaults(); renderFields(); draw(); } return tpl; },
      // parity: {template, picks: {layerId: itemTitle}, texts: {layerId: text}} in one go
      apply: function (s) {
        var v = s.variant != null && (PRODUCT.variants || []).filter(function (x) { return String(x.id) === String(s.variant); })[0];
        if (v) {
          (PRODUCT.options || []).forEach(function (o, i) { selVariant[o.name] = v['option' + (i + 1)]; });
          renderVariants();
          renderPrice();
          if (useArea(areaFor(v))) { settleDefaults(); restage(); }
        }
        if (s.template != null) { tpl = s.template; settleDefaults(); }
        Object.keys(s.picks || {}).forEach(function (id) {
          var l = byId(id);
          if (!l || !l.field) return;
          var it = category(l).items.filter(function (i) { return i.title === s.picks[id] || i.id === s.picks[id]; })[0];
          if (it) { data[id] = { categoryID: l.field.category, id: it.id, key: it.key }; picked[id] = true; }
        });
        Object.keys(s.texts || {}).forEach(function (id) { typed[id] = s.texts[id]; data[id] = { text: s.texts[id] }; });
        renderFields();
        draw();
        return new Promise(function (res) { root.addEventListener('cm:drawn', function () { res(true); }, { once: true }); });
      }
    };

    // A designer opening an order: Ecom Manager links here with the line's `_design` in the
    // hash (#cm-design=<base64url JSON>) and `?variant=`, and the page redraws that design.
    (function restoreFromLink() {
      var vid = new URLSearchParams(location.search).get('variant');
      var v = vid && (PRODUCT.variants || []).filter(function (x) { return String(x.id) === vid; })[0];
      if (v) (PRODUCT.options || []).forEach(function (o, i) { selVariant[o.name] = v['option' + (i + 1)]; });
      if (areas) useArea(areaFor(variantFor(selVariant)));
      var m = /[#&]cm-design=([A-Za-z0-9_-]+)/.exec(location.hash || '');
      if (m) {
        try {
          var b64 = m[1].replace(/-/g, '+').replace(/_/g, '/');
          var d = JSON.parse(decodeURIComponent(escape(atob(b64 + '==='.slice((b64.length + 3) % 4)))));
          // an order made on another garment than the one the link's variant (or the page) opens
          if (d && areas && d.artwork !== aw.id) useArea(areas.filter(function (a) { return a.artwork === d.artwork; })[0]);
          if (d && d.v === 'cm1' && d.artwork === aw.id) {
            if (d.template != null && aw.templates[d.template]) tpl = d.template;
            Object.keys(d.data || {}).forEach(function (id) {
              var v = d.data[id];
              if (v && typeof v === 'object' && typeof v.text === 'string') { typed[id] = v.text; data[id] = { text: v.text }; }
              else if (v && typeof v === 'object' && v.key) { data[id] = v; picked[id] = true; }
            });
            root.setAttribute('data-cm-restored', '1');
          } else console.warn('[cm-builder] cm-design link is for another artwork');
        } catch (e) { console.warn('[cm-builder] unreadable cm-design link', e); }
      }
    })();

    settleDefaults();
    buildStage();
    renderFields();
    renderVariants();
    renderPrice();
    draw();
  }

  /* ------------------------------------------------ Customall's asset urls */
  function customallUrl(key, width) {
    var spec = { key: key };
    if (width) { spec.width = width; spec.unit = 'px'; }
    spec.webp = false;
    var s = unescape(encodeURIComponent(JSON.stringify(spec)));
    var hex = '';
    for (var i = 0; i < s.length; i++) hex += ('0' + (s.charCodeAt(i) ^ 0x47).toString(16)).slice(-2);
    return 'https://assets-v2.customall.io/' + hex;
  }
})();
